import React, { useState, useEffect } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import styled, { keyframes, css } from "styled-components";
import { SG_LOGIN_UPDATE_ACCOUNT_DEFAULT } from "constants/actions";
import { Domain } from "_config";
import UserErrorMessages from "./UserErrorMessages";
import { Message } from "semantic-ui-react";

const SelectOrg = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [orgList, setOrgList] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [pristine, setPristine] = useState(true);
  const [query, setQuery] = useState("");
  const [userAuth, setUserAuth] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeOrgId, setActiveOrgId] = useState(null);

  const { get_auth } = useSelector(
    (state) => ({
      get_auth: state.auth
    }),
    shallowEqual
  );

  useEffect(() => {
    setUserAuth(get_auth);
    setOrgList(get_auth?.organization_list);
    setEmail(get_auth?.user?.email);
  }, [get_auth]);

  useEffect(() => {
    const ErrorList = [];
    let totalErrors = 0;
    if (orgList.length > 0) {
      if (!selectedOrg || !selectedOrg.AccountID) {
        // No error by default; we show per-card actions now.
      }
    }
    ErrorList["errors"] = totalErrors;
    setFormErrors(ErrorList);
  }, [orgList, selectedOrg, pristine]);

  const handleLogin = (org) => {
    if (isSubmitting) return;
    const accountId = org?.AccountID;
    const employeeId = org?.EmployeeID;


    if (!accountId || !employeeId) {
      setPristine(false);
      setFormErrors({
        errors: 1,
        organization: { error: true, message: "Organization data is missing" },
      });
      return;
    }
    setSelectedOrg(org);
    setIsSubmitting(true);
    setActiveOrgId(org?.OrganizationID || org?.AccountID);

    
    dispatch({
      type: SG_LOGIN_UPDATE_ACCOUNT_DEFAULT,
      payload: {
        email: email,
        account_id: accountId,
        employee_id: employeeId,
      },
    });
    setPristine(true);
  };

  // console.log("select org location.state", location.state);
  let { from } = location.state || { from: { pathname: "/simple/dashboard" } };

  // Check if the from pathname is "/settings/select-organization" and update it
  if (from.pathname === "/settings/select-organization") {
    from = { pathname: "/simple/dashboard" };
  }
  if (get_auth?.token.trim() === get_auth?.account_default?.verify_token.trim()) {
    return <Navigate to={from} />;
  }

  // Filter organizations based on query
  const normalized = (v) => (v || "").toString().toLowerCase();

  const filteredOrgs = (orgList || []).filter((org) => {
    const fields = [
      normalized(org?.Name),
      normalized(org?.OrganizationID),
      normalized(org?.Plan || org?.plan),
      normalized(org?.RoleName || org?.role_name || org?.Role || org?.role),
    ];
    const q = normalized(query);
    if (!q) return true;
    return fields.some((f) => f.includes(q));
  });

  return (
    <Container>
      <Content>
        <HeaderArea>
          <Title>Switch Organization</Title>
          <SubTitle>Choose which organization you'd like to work with</SubTitle>
        </HeaderArea>

        <SearchBar>
          <SearchInput
            type="text"
            placeholder="Search organizations by name, plan, or role..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </SearchBar>

        <UserErrorMessages />
        {formErrors?.organization?.error && !pristine && (
          <Message error content={formErrors?.organization?.message} />
        )}

        <Grid>
          {filteredOrgs?.map((org, idx) => {
            const plan = org?.Plan || org?.plan;
            const role = org?.RoleName || org?.role_name || org?.Role || org?.role;
            const memberCount = org?.MemberCount || org?.members || org?.member_count;
            const logo = org?.Logo || org?.logo;
            const initials = (org?.Name || "?")
              .split(" ")
              .map((s) => s[0])
              .slice(0, 2)
              .join("")
              .toUpperCase();
            const isActive = isSubmitting && (activeOrgId === (org?.OrganizationID || org?.AccountID));
            return (
              <Card
                key={`${org?.OrganizationID}-${org?.AccountID}`}
                onClick={() => handleLogin(org)}
                $disabled={isSubmitting}
                $delay={idx * 50}
              >
                <CardHeader>
                  <Avatar>
                    {logo ? <img src={logo} alt={org?.Name} /> : <span>{initials}</span>}
                  </Avatar>
                  <HeaderMeta>
                    <OrgName>{org?.Name}</OrgName>
                  </HeaderMeta>
                </CardHeader>
                <CardBody>
                  <Badges>
                    {plan && <Badge>{plan}</Badge>}
                    {role && <Badge>{role}</Badge>}
                  </Badges>
                </CardBody>
                <CardFooter>
                  {memberCount && <Members>{memberCount} members</Members>}
                  <LoginButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogin(org);
                    }}
                    disabled={isSubmitting}
                  >
                    {isActive ? (
                      <Spinner aria-hidden />
                    ) : (
                      <LoginIcon aria-hidden>↪</LoginIcon>
                    )}
                    {isActive ? "Logging in" : "Login"}
                  </LoginButton>
                </CardFooter>
              </Card>
            );
          })}
        </Grid>
      </Content>
    </Container>
  );
};

export default SelectOrg;

// Motion
const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  font-family: 'Raleway';
  animation: ${fadeInUp} 240ms ease-out both;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1180px;
  padding: 32px 20px 64px;
`;

const HeaderArea = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0 0 6px 0;
  font-weight: 700;
  font-size: 24px;
`;

const SubTitle = styled.div`
  color: #6b7280;
  font-size: 14px;
`;

const SearchBar = styled.div`
  margin: 20px 0 10px 0;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 0 14px;
  font-size: 14px;
  outline: none;
  transition: border-color 120ms ease-in-out, box-shadow 120ms ease-in-out;
  &:focus {
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45,112,226,0.15);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 320px));
  gap: 16px;
  margin-top: 10px;
  justify-content: start;
  @media (max-width: 640px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  padding: 14px;
  cursor: pointer;
  transition: border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease;
  animation: ${fadeInUp} 220ms ease-out both;
  ${(p) =>
    p.$delay !== undefined &&
    css`
      animation-delay: ${p.$delay}ms;
    `}
  &:hover {
    border-color: #d1d5db;
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(0,0,0,0.06);
  }
  &:active {
    transform: translateY(0);
  }
  ${(p) =>
    p.$disabled &&
    css`
      cursor: default;
      opacity: 0.8;
      pointer-events: none;
    `}
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 10px;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111827;
  font-weight: 700;
  font-size: 15px;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const SelectedCheck = styled.div``;

const CardBody = styled.div`
  margin-top: 10px;
`;

const OrgName = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Badges = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #111827;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
`;

const CardFooter = styled.div`
  margin-top: auto;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
`;

const Members = styled.div`
  color: #6b7280;
  font-size: 13px;
`;

const LoginButton = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #111;
  border-radius: 10px;
  background: #111;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
  transition: background-color 120ms ease-in-out, color 120ms ease-in-out, border-color 120ms ease-in-out, opacity 120ms ease-in-out;
  &:hover {
    background: #fff;
    color: #111;
    border-color: #111;
  }
  &:disabled {
    opacity: 0.85;
  }
  justify-self: end;
`;

const LoginIcon = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 2px solid #fff;
  border-top-color: transparent;
  display: inline-block;
  animation: ${spin} 700ms linear infinite;
`;